import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LoginForm as SharedLoginForm,
  type AuthProviderId,
} from '@monorepo/shared-lib';
import Logo from 'assets/images/generateTAX-logo.svg';
import ImgWithGoogleSignIn from 'assets/images/google-icon.png';
import ImgWithMicrosoftSignIn from 'assets/images/microsoft-icon.png';
import ImgWithQboSignIn from 'assets/images/quickbook-icon.png';
import ImgWithXeroSignIn from 'assets/images/xero_icon.png';
import ImgWithZohoSignIn from 'assets/images/zoho_icon.png';
import SignInWithQbo from 'assets/images/Sign_in_transparent_btn_med_default.svg';
import routes from 'routes';
import { Provider } from 'store/api/enums';
import { MfaResponse } from 'store/auth/models';
import { isZohoEnabled } from 'utils/feature-flag-utils';
import { reapplyThemeRespectingA11y } from 'utils/theme-preference';

interface LoginFormProps {
  onLogin: (
    email: string,
    password: string,
    gCaptchaResponse: string,
    mfaCode: string | null
  ) => void;
  loading?: boolean;
  onProviderLogin: (provider: Provider) => void;
  mfaResponse?: MfaResponse;
}

const providerMap: Record<AuthProviderId, Provider> = {
  xero: Provider.XERO,
  google: Provider.GOOGLE,
  azure: Provider.AZURE,
  quickbooks: Provider.QUICKBOOKS,
  zoho: Provider.ZOHO,
};

export const LoginForm: React.FC<LoginFormProps> = ({
  onLogin,
  loading,
  onProviderLogin,
  mfaResponse,
}) => {
  const { t } = useTranslation();

  return (
    <SharedLoginForm
      onLogin={onLogin}
      onProviderLogin={(provider) => onProviderLogin(providerMap[provider])}
      loading={loading}
      mfaResponse={mfaResponse}
      labels={{
        welcome: t('login.welcome'),
        siteTitle: t('common.site-title'),
        email: t('signup.email-address'),
        password: t('signup.password'),
        forgotPassword: t('login.forgot-password'),
        login: t('login.login'),
        or: t('signup.OR'),
        xeroLogin: t('login.xero-login'),
        googleLogin: t('login.google-login'),
        microsoftLogin: t('login.microsoft-login'),
        qboLogin: t('login.qbo-login'),
        zohoLogin: t('login.zoho-login'),
        noAccount: t('login.donot-have-account'),
        signup: t('login.signup'),
        mfaPrompt: t('other.mfa-code-prompt'),
        mfaCodeLabel: t('login.mfa-code-label'),
        requiredField: t('common.required-field'),
        invalidEmail: t('signup.invalid-email'),
        confirm: t('common.confirm'),
        backToLogin: t('forgot-password.back-to-login'),
        logoAriaLabel: t('login.logo-aria-label'),
        accessibilityMode: t('login.accessibility-mode'),
        accessibilityModeHelp: t('login.accessibility-mode-help'),
      }}
      assets={{
        logo: Logo,
        xero: ImgWithXeroSignIn,
        google: ImgWithGoogleSignIn,
        microsoft: ImgWithMicrosoftSignIn,
        qbo: ImgWithQboSignIn,
        qboHover: SignInWithQbo,
        zoho: ImgWithZohoSignIn,
      }}
      routes={{
        login: routes.login,
        main: routes.main,
        signUp: routes.signUp.signupForm,
        signUpRoot: routes.signUp.root,
        resetPassword: routes.resetPassword.request,
        resetPasswordRoot: routes.resetPassword.root,
      }}
      captchaSiteKey={process.env.G_CAPTCHA_SITE_KEY || ''}
      showZoho={isZohoEnabled()}
      showA11yToggle
      onA11yDisabled={reapplyThemeRespectingA11y}
    />
  );
};
